/*
 * LogisticsHomePage — the app landing screen inside the Hub.
 *
 * Bento grid of six service tiles + hero-copy left column that pitches the
 * app's purpose (mission-critical, high-friction financial simulation).
 * All 6 tiles are duotone Phosphor icons in the Electric Copper accent.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Bed, ForkKnife, Truck, AirplaneTilt, ShoppingCart, Wallet,
  ArrowRight, Sparkle, Lightning,
} from '@phosphor-icons/react';

import LogisticsLayout, { formatMinor } from './LogisticsLayout';
import { useLogisticsExercise } from '../../context/LogisticsExerciseContext';
import logisticsApi from '../../lib/logisticsApi';

const TILES = [
  {
    to: '/hub/logistics/accommodation',
    label: 'Accommodation',
    tag: 'STAY · CHECK-IN · CANCEL',
    copy: 'Hotels, B&Bs, emergency shelters. Real-time capacity as teams book.',
    Icon: Bed,
    testid: 'ls-tile-accommodation',
  },
  {
    to: '/hub/logistics/food',
    label: 'Food & Catering',
    tag: 'MEALS · BULK · DIETARY',
    copy: 'Restaurant orders and field-team catering, delivery windows honoured.',
    Icon: ForkKnife,
    testid: 'ls-tile-food',
  },
  {
    to: '/hub/logistics/transport',
    label: 'Ground Transport',
    tag: 'ROUTES · CAPACITY · SURGE',
    copy: 'Cars, coaches, ferries, trains. SimCell can close routes on the fly.',
    Icon: Truck,
    testid: 'ls-tile-transport',
  },
  {
    to: '/hub/logistics/flights',
    label: 'Flights',
    tag: 'CHARTER · SEAT · WEATHER',
    copy: 'Charter, commercial, helicopter. The budget-eating option.',
    Icon: AirplaneTilt,
    testid: 'ls-tile-flights',
  },
  {
    to: '/hub/logistics/procurement',
    label: 'Procurement',
    tag: 'PPE · OIL-SPILL · INFRA',
    copy: 'Exercise supply chain: order what your stock doesn\'t cover.',
    Icon: ShoppingCart,
    testid: 'ls-tile-procurement',
  },
  {
    to: '/hub/logistics/wallet',
    label: 'Wallet & Ledger',
    tag: 'BALANCE · CARDS · AUDIT',
    copy: 'Simulated cards, live balance, immutable spend ledger.',
    Icon: Wallet,
    testid: 'ls-tile-wallet',
  },
];

const HomeContent = () => {
  const navigate = useNavigate();
  const { selectedId: exerciseId } = useLogisticsExercise();
  const [walletBalance, setWalletBalance] = useState(0);
  const [symbol, setSymbol] = useState('Ÿ');
  const [walletsError, setWalletsError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const w = await logisticsApi.myWallets();
        const all = [...(w.personal || []), ...(w.team || [])];
        const scoped = exerciseId ? all.filter(x => x.logistics_exercise_id === exerciseId) : all;
        setWalletBalance(scoped.reduce((s, x) => s + (x.available || 0), 0));
      } catch {
        setWalletsError(true);
      }
    })();
  }, [exerciseId]);

  return (
    <>
      {/* HERO — bold Cabinet Grotesk headline + Satoshi sub */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
        <div className="lg:col-span-8">
          <span className="ls-overline flex items-center gap-2 mb-4" style={{ color: 'var(--ls-primary)' }}>
            <Lightning size={12} weight="fill" /> LOGISTICS & SPEND COMMAND
          </span>
          <h1 className="ls-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02] mb-5">
            Every credit spent<br />is a decision made.
          </h1>
          <p className="text-base leading-relaxed max-w-2xl" style={{ color: 'var(--ls-muted)' }}>
            A live logistics super-app for ICS 300 field exercises. Six services,
            one wallet, one immutable ledger. Real card muscle-memory — simulated
            money. SimCell can surge, freeze, or cut budgets in a single click.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button className="ls-btn-primary" onClick={() => navigate('/hub/logistics/wallet')} data-testid="ls-cta-wallet">
              Open Wallet <ArrowRight size={14} weight="bold" />
            </button>
            <button className="ls-btn-ghost" onClick={() => navigate('/hub/logistics/procurement')} data-testid="ls-cta-procurement">
              <Sparkle size={14} weight="fill" /> Browse Catalogue
            </button>
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="ls-elevated ls-noise p-6 h-full flex flex-col justify-between">
            <div>
              <p className="ls-overline mb-3">Combined Balance</p>
              <p className="ls-mono text-4xl font-bold" style={{ color: 'var(--ls-primary)' }}>
                {symbol}{formatMinor(walletBalance)}
              </p>
              <p className="text-xs mt-2" style={{ color: 'var(--ls-muted)' }}>
                {walletsError ? 'No wallets provisioned yet.' : 'Personal + team pools combined.'}
              </p>
            </div>
            <div className="mt-6 h-24 relative overflow-hidden rounded-sm" style={{ background: 'var(--ls-secondary)', opacity: 0.06 }}>
              {/* Ledger sparkline placeholder — real chart in P4 */}
              <svg viewBox="0 0 200 80" preserveAspectRatio="none" className="w-full h-full">
                <polyline
                  fill="none"
                  stroke="var(--ls-primary)"
                  strokeWidth="1.5"
                  points="0,60 20,55 40,50 60,55 80,40 100,45 120,25 140,30 160,20 180,25 200,15"
                />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* BENTO GRID — 6 service tiles, duotone icons */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <h2 className="ls-heading text-2xl font-semibold tracking-tight">Services</h2>
          <span className="ls-overline">6 modules · all live</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map(({ to, label, tag, copy, Icon, testid }, i) => (
            <motion.button
              key={to}
              onClick={() => navigate(to)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="ls-tile text-left"
              data-testid={testid}
            >
              <div className="flex items-start justify-between mb-6 relative z-10">
                <Icon size={40} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
                <ArrowRight
                  size={18}
                  weight="bold"
                  style={{ color: 'var(--ls-muted)' }}
                  className="opacity-40"
                />
              </div>
              <div className="relative z-10">
                <p className="ls-overline mb-2">{tag}</p>
                <h3 className="ls-heading text-xl font-semibold mb-1">{label}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--ls-muted)' }}>
                  {copy}
                </p>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Divider */}
      <div className="my-14 h-px" style={{ background: 'var(--ls-border)' }} />

      {/* Elevator pitch strip — reinforces "Tactical Finance" tone */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { k: 'Cards', v: 'Look like Visa, resolve to our wallet. Number, CVV, expiry, all real-typing.', testid: 'ls-pitch-cards' },
          { k: 'SimCell', v: 'Surge pricing, stock shortages, budget cuts — one click, live to every screen.', testid: 'ls-pitch-simcell' },
          { k: 'Ledger', v: 'Append-only. Every hold, capture, refund with the SimCell fingerprint that caused it.', testid: 'ls-pitch-ledger' },
        ].map((p) => (
          <div key={p.k} className="ls-surface p-6" data-testid={p.testid}>
            <p className="ls-overline mb-2">{p.k}</p>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--ls-secondary)' }}>{p.v}</p>
          </div>
        ))}
      </section>
    </>
  );
};

const LogisticsHomePage = () => (
  <LogisticsLayout>
    <HomeContent />
  </LogisticsLayout>
);

export default LogisticsHomePage;
